/**
 * Shared page header primitives — used across Research, Builder, and Insights pages
 * so the back button and title are visually identical everywhere.
 */
import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from './theme'

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.lg};
  margin-bottom: ${SPACING.xl};
`

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${BORDER_RADIUS.md};
  color: var(--color-text-secondary);
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
`

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

export const PageTitle = styled.h1`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const PageSubtitle = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

/**
 * Inner @media print rules that force light-mode appearance regardless of the
 * user's active theme. Shared by every page that supports PDF export so the
 * dark-mode → print-as-light behavior stays consistent.
 *
 * Wrap this in `@media print { ... }` plus any page-specific @page rules.
 */
export const PRINT_LIGHT_MODE_OVERRIDES = `
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    .no-print {
      display: none !important;
    }
    header, .ant-layout-header {
      display: none !important;
    }
    .ant-layout-content {
      margin-top: 0 !important;
    }
    html, body {
      height: auto;
    }

    /* Force light mode in print/PDF regardless of user theme */
    body, body[data-theme='dark'] {
      --color-bg-primary: #ffffff;
      --color-bg-secondary: #f9fafa;
      --color-bg-elevated: #ffffff;
      --color-bg-hover: #f5f5f5;
      --color-bg-card: #ffffff;
      --color-text-primary: #000000;
      --color-text-secondary: #4b5563;
      --color-text-tertiary: #6b7280;
      --color-text-muted: #8c8c8c;
      --color-border: #f0f0f0;
      --color-border-dark: #d1d5db;
      --color-border-card: #e5e7eb;
      --color-primary: #6e53f9;
      --color-primary-bg: rgba(110, 83, 249, 0.1);
      --color-error: #ff4d4f;
      --color-input-bg: #ffffff;
    }

    /* Antd Typography sets its own color via ConfigProvider theme tokens,
       which bypass our CSS variables. Force dark text in print so it stays
       readable on the forced white background. */
    body[data-theme='dark'] .ant-typography,
    body[data-theme='dark'] .ant-typography p,
    body[data-theme='dark'] .ant-typography span,
    body[data-theme='dark'] h1.ant-typography,
    body[data-theme='dark'] h2.ant-typography,
    body[data-theme='dark'] h3.ant-typography,
    body[data-theme='dark'] h4.ant-typography,
    body[data-theme='dark'] h5.ant-typography {
      color: #000000 !important;
    }
    body[data-theme='dark'] .ant-typography.ant-typography-secondary {
      color: #4b5563 !important;
    }
`

/**
 * Shared @media print CSS string for pages that support PDF export.
 * Uses A4 page size with 2cm margins. For custom @page rules, compose your
 * own block and embed PRINT_LIGHT_MODE_OVERRIDES inside @media print.
 */
export const PRINT_STYLES = `
  @page {
    size: A4;
    margin: 2cm;
  }
  @media print {
    body {
      margin: 0;
      padding: 0;
    }
    ${PRINT_LIGHT_MODE_OVERRIDES}
  }
`
